import {
  CustomField,
  getCustomField as getQuery,
  createCustomField as createMutation,
  updateCustomField as updateMutation,
  deleteCustomField as deleteMutation,
  deleteCustomFieldDeep as deleteCustomFieldDeepMutation,
  CreateCustomFieldMutationVariables,
  DeleteCustomFieldMutationVariables,
  DeleteCustomFieldDeepMutationVariables,
  MutationStatus,
  CreateCustomFieldInput,
  cleanInputUpdate,
  cleanInputCreate,
  getReadId,
  getTableClientId,
  OnCreateCustomFieldSubscription,
  OnUpdateCustomFieldSubscription,
  OnDeleteCustomFieldSubscription,
  onCreateCustomField,
  onUpdateCustomField,
  onDeleteCustomField,
  Model,
  addCustomFieldProperties,
  syncCustomFields,
  UpdateCustomFieldInput,
  buildReorderedCustomFieldsPayload,
  sortCustomFieldsByOrder,
} from '@rentguru/commons-utils';
import {
  cleanMixedDatastoreAndAPISubscription,
  deleteEntityWithFetchBefore,
  getFilterFieldNameForIndex,
  list,
  mutation,
  useSubscriptions,
} from '@up2rent/graphql';
import isNil from 'lodash/isNil';
import { createContext, Reducer, useContext, useEffect, useMemo, useReducer, useRef } from 'react';

import { useUser } from './UserContext';

const ENTITY_MODEL_NAME: Model = 'CustomField';

const customSyncCustomFields = addCustomFieldProperties(syncCustomFields);
const customUpdateMutation = addCustomFieldProperties(updateMutation);
const customOnUpdate = addCustomFieldProperties(onUpdateCustomField);
const customCreateMutation = addCustomFieldProperties(createMutation);
const customOnCreate = addCustomFieldProperties(onCreateCustomField);

export interface CustomFieldsContextState extends CustomFieldsState {
  createCustomField: (input: Omit<CreateCustomFieldInput, 'id' | 'clientId' | 'readId'>) => Promise<CustomField>;
  getCustomFieldsByForeignKey: (foreignKey: string) => CustomField[];
  getCustomField: (id: string) => CustomField | undefined;
  updateCustomField: (input: UpdateCustomFieldInput) => Promise<CustomField>;
  deleteCustomField: (id: string) => Promise<CustomField | undefined>;
  deleteCustomFieldDeep: (id: string) => Promise<boolean>;
  reorderCustomFields: (orderedFields: CustomField[]) => Promise<boolean>;
  setFetchCustomFields: () => void;
}

interface CustomFieldsState {
  customFields: CustomField[] | null;
  loading: boolean;
  shouldFetch: boolean;
}

type Action =
  | { type: 'SHOULD_FETCH' | 'IS_FETCHING' }
  | { type: 'ADD_CUSTOM_FIELD'; payload: CustomField }
  | { type: 'FETCHED'; payload: { customFields: CustomField[] } }
  | { type: 'UPDATE_CUSTOM_FIELD'; payload: CustomField }
  | { type: 'DELETE_CUSTOM_FIELD'; payload: { id: string } }
  | { type: 'FETCH_FAILED' };

const initialState: CustomFieldsState = {
  loading: false,
  customFields: null,
  shouldFetch: false,
};

const customFieldReducer = (state: CustomFieldsState, action: Action): CustomFieldsState => {
  switch (action.type) {
    case 'SHOULD_FETCH':
      if (state.loading || state.shouldFetch) return state;
      return { ...state, shouldFetch: true };
    case 'IS_FETCHING':
      return { ...state, loading: true };
    case 'FETCHED':
      return {
        ...state,
        loading: false,
        shouldFetch: false,
        customFields: action.payload.customFields,
      };
    case 'FETCH_FAILED':
      return { ...state, loading: false, shouldFetch: false };
    case 'ADD_CUSTOM_FIELD':
      if (state.customFields?.find((field) => field.id === action.payload.id)) return state;
      return {
        ...state,
        customFields: [...(state.customFields ?? []), action.payload],
      };
    case 'UPDATE_CUSTOM_FIELD':
      if (!state.customFields) return state;
      return {
        ...state,
        customFields: state.customFields.map((field) => (field.id === action.payload.id ? action.payload : field)),
      };
    case 'DELETE_CUSTOM_FIELD':
      if (!state.customFields) return state;
      return {
        ...state,
        customFields: state.customFields.filter((field) => field.id !== action.payload.id),
      };
    default:
      return state;
  }
};

export const CustomFieldsContext = createContext<CustomFieldsContextState | null>(null);

export const CustomFieldsContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer<Reducer<CustomFieldsState, Action>>(customFieldReducer, initialState);
  const { clientId } = useUser();

  const setFetchCustomFields = () => dispatch({ type: 'SHOULD_FETCH' });

  useEffect(() => {
    const fetchAndSet = async () => {
      if (!clientId) return;
      dispatch({ type: 'IS_FETCHING' });

      try {
        const result = await list<CustomField>(
          customSyncCustomFields,
          getFilterFieldNameForIndex('byClientId'),
          getTableClientId(clientId, ENTITY_MODEL_NAME)
        );
        dispatch({ type: 'FETCHED', payload: { customFields: result } });
      } catch (error) {
        console.error('[customFields] fetching custom fields failed', error);
        dispatch({ type: 'FETCH_FAILED' });
      }
    };

    if (state.shouldFetch) fetchAndSet();
  }, [state.shouldFetch, clientId]);

  useSubscriptions<OnCreateCustomFieldSubscription, OnUpdateCustomFieldSubscription, OnDeleteCustomFieldSubscription>(
    cleanMixedDatastoreAndAPISubscription(customOnCreate),
    cleanMixedDatastoreAndAPISubscription(customOnUpdate),
    cleanMixedDatastoreAndAPISubscription(onDeleteCustomField),
    (data) => dispatch({ type: 'ADD_CUSTOM_FIELD', payload: data.onCreateCustomField as CustomField }),
    (data) => dispatch({ type: 'UPDATE_CUSTOM_FIELD', payload: data.onUpdateCustomField as CustomField }),
    (data) => {
      const { id } = data.onDeleteCustomField as CustomField;
      dispatch({ type: 'DELETE_CUSTOM_FIELD', payload: { id } });
    }
  );

  const createCustomField = async (
    input: Omit<CreateCustomFieldInput, 'id' | 'clientId' | 'readId'>
  ): Promise<CustomField> => {
    if (!clientId) throw new Error('Cannot create Custom Field without a valid clientId');
    const customField = await mutation<CustomField, CreateCustomFieldMutationVariables>(customCreateMutation, {
      input: {
        ...(cleanInputCreate(input) as CreateCustomFieldInput),
        clientId: getTableClientId(clientId, ENTITY_MODEL_NAME),
        readId: getReadId(clientId, ENTITY_MODEL_NAME),
      },
    });
    dispatch({ type: 'ADD_CUSTOM_FIELD', payload: customField });
    return customField;
  };

  const updateCustomField = async (input: UpdateCustomFieldInput): Promise<CustomField> => {
    const result = await mutation<CustomField>(customUpdateMutation, {
      input: cleanInputUpdate(input, false),
    });
    dispatch({ type: 'UPDATE_CUSTOM_FIELD', payload: result });
    return result;
  };

  const deleteCustomField = async (id: string): Promise<CustomField | undefined> => {
    const customField = getCustomField(id);
    if (!customField) return;
    await deleteEntityWithFetchBefore<Pick<CustomField, 'id'>, DeleteCustomFieldMutationVariables>(
      { id },
      getQuery,
      deleteMutation
    );
    dispatch({ type: 'DELETE_CUSTOM_FIELD', payload: { id } });
    return customField;
  };

  const deleteCustomFieldDeep = async (id: string): Promise<boolean> => {
    const { status } = await mutation<MutationStatus, DeleteCustomFieldDeepMutationVariables>(
      deleteCustomFieldDeepMutation,
      { input: { id } }
    );
    const deleted = status ?? false;
    if (deleted) {
      dispatch({ type: 'DELETE_CUSTOM_FIELD', payload: { id } });
    }
    return deleted;
  };

  const getCustomField = (id: string) => (state.customFields ?? []).find((field) => field.id === id);
  const getCustomFieldsByForeignKey = (foreignKey: string) => {
    if (isNil(state.customFields)) return [];
    return sortCustomFieldsByOrder(state.customFields.filter((field) => field.foreignKey === foreignKey));
  };

  const reorderCustomFields = async (orderedFields: CustomField[]): Promise<boolean> => {
    const payloads = buildReorderedCustomFieldsPayload(orderedFields);
    const results = await Promise.allSettled(payloads.map((input) => updateCustomField(input)));
    if (results.every((result) => result.status === 'fulfilled')) return true;

    const originalFieldsById = new Map(orderedFields.map((field) => [field.id, field]));
    const rollbackPayloads = results.reduce<UpdateCustomFieldInput[]>((accumulator, result, index) => {
      if (result.status !== 'fulfilled') {
        return accumulator;
      }

      const originalField = originalFieldsById.get(payloads[index].id);
      if (!originalField) {
        return accumulator;
      }

      accumulator.push({
        id: result.value.id,
        order: originalField.order,
        _version: result.value._version,
      });
      return accumulator;
    }, []);

    await Promise.allSettled(rollbackPayloads.map((input) => updateCustomField(input)));
    setFetchCustomFields();
    return false;
  };

  const values = useMemo(
    () => ({
      ...state,
      createCustomField,
      updateCustomField,
      deleteCustomField,
      deleteCustomFieldDeep,
      getCustomField,
      getCustomFieldsByForeignKey,
      reorderCustomFields,
      setFetchCustomFields,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <CustomFieldsContext.Provider value={values}>{children}</CustomFieldsContext.Provider>;
};

export const useCustomFields = (): CustomFieldsContextState => {
  const firstRender = useRef<boolean>(true);
  const context = useContext<CustomFieldsContextState | null>(CustomFieldsContext);

  if (isNil(context)) {
    throw new Error('`useCustomFields` hook must be used within a `CustomFieldsContextProvider` component');
  }

  useEffect(() => {
    if (firstRender.current && isNil(context.customFields)) {
      context.setFetchCustomFields();
      firstRender.current = false;
    }
  }, [context]);

  return context;
};
